import React from 'react';
import { Button } from './ui/button';
import { FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Generic export button component
export const ExportExcelButton = ({ data, filename, sheetName = 'Datos', disabled = false }) => {
  const handleExport = () => {
    if (!data || data.length === 0) return;
    
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(data);
    
    // Auto-size columns
    const maxWidths = data[0].map((_, colIndex) => {
      return Math.max(...data.map(row => {
        const cell = row[colIndex];
        return cell ? String(cell).length : 10;
      }));
    });
    ws['!cols'] = maxWidths.map(w => ({ wch: Math.min(w + 2, 50) }));
    
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${filename}.xlsx`);
  };

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleExport}
      disabled={disabled || !data || data.length === 0}
      className="gap-2"
    >
      <FileSpreadsheet className="w-4 h-4" />
      Excel
    </Button>
  );
};

// Export investments data
export const exportInvestmentsToExcel = (investments, projectName, currency = 'MXN') => {
  if (!investments || investments.length === 0) return;
  
  const data = [
    ['INVERSIONES - ' + projectName],
    [''],
    ['#', 'Concepto', 'Monto', 'Vida Útil (años)', '% Depreciación', 'Valor Rescate', 'Se Revalúa', 'Tasa Reval. (%)'],
    ...investments.map((inv, idx) => [
      idx + 1,
      inv.concept,
      inv.amount,
      inv.depreciation_years,
      inv.has_revaluation ? 'N/A (Revalúa)' : inv.depreciation_years > 0 ? (100 / inv.depreciation_years).toFixed(1) + '%' : '-',
      inv.include_salvage !== false ? 'Sí' : 'No',
      inv.has_revaluation ? 'Sí' : 'No',
      inv.has_revaluation ? inv.revaluation_rate + '%' : '-'
    ]),
    [''],
    ['TOTAL', '', investments.reduce((sum, i) => sum + (i.amount || 0), 0)]
  ];
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 5 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 15 }, { wch: 12 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Inversiones');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Inversiones.xlsx`);
};

// Export financing data
export const exportFinancingToExcel = (financing, projectName, currency = 'MXN') => {
  if (!financing) return;
  
  const data = [
    ['FINANCIAMIENTO - ' + projectName],
    [''],
    ['ESTRUCTURA DE CAPITAL'],
    ['Inversión Total', financing.total_investment],
    [''],
    ['DEUDA'],
    ['Monto de Deuda', financing.debt_amount],
    ['% del Total', (financing.debt_percentage || 0) + '%'],
    ['Costo de Deuda', (financing.cost_of_debt || 0) + '%'],
    ['Plazo (años)', financing.debt_term_years],
    [''],
    ['CAPITAL PROPIO'],
    ['Monto de Capital', financing.equity_amount],
    ['% del Total', (financing.equity_percentage || 0) + '%'],
    ['Costo del Capital', (financing.cost_of_equity || 0) + '%'],
    [''],
    ['WACC', (financing.wacc || 0).toFixed(2) + '%']
  ];
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 25 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Financiamiento');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Financiamiento.xlsx`);
};

// Export products/sales volume data
export const exportSalesVolumeToExcel = (products, projectName, years, daysPerYear, currency = 'MXN', operatingMonths = null) => {
  if (!products || products.length === 0) return;
  
  const daysPerMonth = daysPerYear / 12;
  const opMonths = operatingMonths || Array(years).fill(12);
  
  const headers = ['Producto', 'Vol. Base', 'Frecuencia', 'Tipo Crecimiento', '% Crecimiento'];
  for (let i = 1; i <= years; i++) {
    headers.push(`Año ${i}${opMonths[i-1] !== 12 ? ` (${opMonths[i-1]}m)` : ''}`);
  }
  
  const data = [
    ['VOLUMEN DE VENTAS - ' + projectName],
    [''],
    headers,
    ...products.map(p => {
      const row = [
        p.name,
        p.base_volume,
        p.input_frequency === 'daily' ? 'Diario' : 'Mensual',
        p.growth_type === 'fixed' ? 'Fijo' : p.growth_type === 'growth' ? 'Crecimiento' : 'Manual',
        (p.growth_percentage || 0) + '%'
      ];
      
      // Calculate projections using operating months
      for (let i = 1; i <= years; i++) {
        const om = opMonths[i - 1] || 12;
        const baseAnnual = p.input_frequency === 'daily' 
          ? p.base_volume * daysPerMonth * om 
          : p.base_volume * om;
        
        let yearVolume = baseAnnual;
        if (p.growth_type === 'growth') {
          yearVolume = baseAnnual * Math.pow(1 + (p.growth_percentage || 0) / 100, i - 1);
        } else if (p.growth_type === 'decline') {
          yearVolume = baseAnnual * Math.pow(1 - (p.growth_percentage || 0) / 100, i - 1);
        }
        row.push(Math.round(yearVolume));
      }
      
      return row;
    })
  ];
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, ws, 'Volumen de Ventas');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Volumen_Ventas.xlsx`);
};

// Export pricing data
export const exportPricingToExcel = (products, projectName, currency = 'MXN') => {
  if (!products || products.length === 0) return;
  
  const data = [
    ['PRECIOS Y COSTOS - ' + projectName],
    [''],
    ['Producto', 'Precio', 'Costo Total', 'Margen', '% Margen'],
    ...products.map(p => {
      const totalCost = p.costs?.reduce((sum, c) => sum + (c.amount || 0), 0) || p.unit_cost || 0;
      const margin = (p.unit_price || 0) - totalCost;
      const marginPct = p.unit_price > 0 ? (margin / p.unit_price * 100) : 0;
      return [
        p.name,
        p.unit_price || 0,
        totalCost,
        margin,
        marginPct.toFixed(1) + '%'
      ];
    }),
    [''],
    ['DESGLOSE DE COSTOS POR PRODUCTO'],
    ['']
  ];
  
  // Add cost breakdown for each product
  products.forEach(p => {
    data.push([p.name]);
    (p.costs || []).forEach(c => {
      data.push(['  - ' + (c.name || 'Sin nombre'), c.amount || 0]);
    });
    data.push(['']);
  });
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Precios y Costos');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Precios_Costos.xlsx`);
};

// Export fixed expenses data
export const exportFixedExpensesToExcel = (expenses, projectName, years, currency = 'MXN') => {
  if (!expenses || expenses.length === 0) return;
  
  const headers = ['Concepto', 'Monto Mensual', 'Inicio (Año)', 'Crecimiento'];
  for (let i = 1; i <= years; i++) {
    headers.push(`Año ${i}`);
  }
  
  const data = [
    ['GASTOS FIJOS - ' + projectName],
    [''],
    headers,
    ...expenses.map(e => {
      const row = [
        e.concept,
        e.monthly_amount || 0,
        e.start_year || 1,
        (e.growth_rate || 0) + '%'
      ];
      
      for (let i = 1; i <= years; i++) {
        if (i < (e.start_year || 1)) {
          row.push(0);
        } else {
          const yearsActive = i - (e.start_year || 1);
          const annualAmount = (e.monthly_amount || 0) * 12 * Math.pow(1 + (e.growth_rate || 0) / 100, yearsActive);
          row.push(Math.round(annualAmount));
        }
      }
      
      return row;
    })
  ];
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  XLSX.utils.book_append_sheet(wb, ws, 'Gastos Fijos');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Gastos_Fijos.xlsx`);
};

// Export scenarios data
export const exportScenariosToExcel = (scenarios, breakeven, projectName, currency = 'MXN', backendScenarios = null) => {
  const data = [
    ['ANÁLISIS DE ESCENARIOS - ' + projectName],
    [''],
    ['PUNTO DE EQUILIBRIO'],
    ['Unidades para Equilibrio', breakeven?.breakeven_units || 0],
    ['Ingresos para Equilibrio', breakeven?.breakeven_revenue || 0],
    ['Margen de Seguridad', (breakeven?.margin_of_safety * 100 || 0).toFixed(1) + '%'],
    [''],
    ['ESCENARIOS'],
    ['Escenario', 'VAN', 'TIR', 'Veredicto'],
    ['Pesimista', scenarios?.pessimistic?.npv || 0, (scenarios?.pessimistic?.irr * 100 || 0).toFixed(1) + '%', scenarios?.pessimistic?.npv > 0 ? 'Aceptado' : 'Rechazado'],
    ['Base', scenarios?.base?.npv || 0, (scenarios?.base?.irr * 100 || 0).toFixed(1) + '%', scenarios?.base?.npv > 0 ? 'Aceptado' : 'Rechazado'],
    ['Optimista', scenarios?.optimistic?.npv || 0, (scenarios?.optimistic?.irr * 100 || 0).toFixed(1) + '%', scenarios?.optimistic?.npv > 0 ? 'Aceptado' : 'Rechazado']
  ];

  // Add perpetuity comparison if active
  if (backendScenarios?.perpetuity_active) {
    const bs = backendScenarios.scenarios || {};
    data.push(['']);
    data.push(['IMPACTO DE LA PERPETUIDAD EN ESCENARIOS']);
    data.push(['Escenario', 'VAN con Perpetuidad', 'TIR con Perpetuidad', 'VAN sin Perpetuidad', 'TIR sin Perpetuidad', 'Valor Terminal']);
    for (const [key, label] of [['optimistic', 'Optimista'], ['base', 'Base'], ['pessimistic', 'Pesimista']]) {
      const s = bs[key];
      if (s) {
        data.push([
          label,
          s.npv || 0,
          (s.irr || 0).toFixed(1) + '%',
          s.npv_without_perpetuity || 0,
          (s.irr_without_perpetuity || 0).toFixed(1) + '%',
          s.terminal_value || 0
        ]);
      }
    }
  }
  
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 25 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 22 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, ws, 'Escenarios');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Escenarios.xlsx`);
};

// Export income statement data
export const exportIncomeStatementToExcel = (years, projectName, currency = 'MXN') => {
  if (!years || years.length === 0) return;

  const headers = ['Concepto', ...years.map(y => `Año ${y.year}`)];
  const pct = (val, rev) => rev > 0 ? ((val / rev) * 100).toFixed(1) + '%' : '0%';

  const data = [
    ['ESTADO DE RESULTADOS - ' + projectName],
    ['Moneda: ' + currency],
    [''],
    headers,
    ['Ingresos por Ventas', ...years.map(y => Math.round(y.revenue))],
    ['Costo de Ventas (COGS)', ...years.map(y => Math.round(y.cogs))],
    ['Utilidad Bruta', ...years.map(y => Math.round(y.gross_margin))],
    ['  % Margen Bruto', ...years.map(y => pct(y.gross_margin, y.revenue))],
    [''],
    ['Gastos Operativos', ...years.map(y => Math.round(y.operating_expenses))],
    ['EBITDA', ...years.map(y => Math.round(y.ebitda))],
    ['  % Margen EBITDA', ...years.map(y => pct(y.ebitda, y.revenue))],
    [''],
    ['Depreciación', ...years.map(y => Math.round(y.depreciation))],
    ['EBIT', ...years.map(y => Math.round(y.ebit))],
    ['  % Margen EBIT', ...years.map(y => pct(y.ebit, y.revenue))],
    [''],
    ['Gastos por Intereses', ...years.map(y => Math.round(y.interest_expense))],
    ['UAI (EBT)', ...years.map(y => Math.round(y.ebt))],
    ['  % Margen EBT', ...years.map(y => pct(y.ebt, y.revenue))],
    [''],
    ['Impuestos', ...years.map(y => Math.round(y.taxes))],
    ['UTILIDAD NETA', ...years.map(y => Math.round(y.net_income))],
    ['  % Margen Neto', ...years.map(y => pct(y.net_income, y.revenue))],
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 25 }, ...years.map(() => ({ wch: 18 }))];
  XLSX.utils.book_append_sheet(wb, ws, 'Estado de Resultados');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Estado_Resultados.xlsx`);
};

// Export cash flow data
export const exportCashFlowToExcel = (flows, projectName, currency = 'MXN') => {
  if (!flows || flows.length === 0) return;

  const headers = ['Concepto', ...flows.map(f => f.year === 0 ? 'Año 0' : `Año ${f.year}`)];

  const row = (label, key) => [label, ...flows.map(f => Math.round(f[key] || 0))];

  const data = [
    ['FLUJO DE EFECTIVO - ' + projectName],
    ['Moneda: ' + currency],
    [''],
    headers,
    ['--- FLUJO OPERATIVO ---'],
    row('EBITDA', 'ebitda'),
    row('Impuestos', 'taxes'),
    row('Flujo Operativo', 'operating_cash_flow'),
    [''],
    ['--- FLUJO DE INVERSIÓN ---'],
    row('CAPEX', 'capex'),
    row('Valor de Rescate', 'salvage_value'),
    row('Flujo Inversión', 'investing_cash_flow'),
    [''],
    ['--- FLUJO DE FINANCIAMIENTO ---'],
    row('Aportación de Capital', 'equity_contribution'),
    row('Préstamos', 'loan_proceeds'),
    row('Pago de Intereses', 'interest_payment'),
    row('Pago de Principal', 'principal_payment'),
    row('Flujo Financiamiento', 'financing_cash_flow'),
    [''],
    ['--- RESUMEN ---'],
    row('Flujo Neto del Periodo', 'net_cash_flow'),
    row('Flujo Acumulado', 'cumulative_cash_flow'),
    [''],
    ['--- FLUJO LIBRE ---'],
    row('FCF Proyecto', 'project_cash_flow'),
    row('FCF Acumulado Proyecto', 'cumulative_project_cf'),
    row('FCF Accionista', 'equity_cash_flow'),
    row('FCF Acumulado Accionista', 'cumulative_equity_cf'),
  ];

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.aoa_to_sheet(data);
  ws['!cols'] = [{ wch: 28 }, ...flows.map(() => ({ wch: 18 }))];
  XLSX.utils.book_append_sheet(wb, ws, 'Flujo de Efectivo');
  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  saveAs(new Blob([wbout], { type: 'application/octet-stream' }), `${projectName}_Flujo_Efectivo.xlsx`);
};
